/** TOTOKA Journey V2 S26 — dependency-free, native scroll, one decoder.
 * Soft arrival: decoded room + pearl light passage + staggered foreground.
 * Model units: 1 = 1svh of scroll. Media never determines timeline duration.
 * One native scroll gesture selects one step; room travel plays to completion.
 * Scroll source is the nearest native scroll container (or window).
 * No wheel interception. Playback and residual scroll cannot queue another step.
 */
export const clamp = (n, low = 0, high = 1) => Math.min(high, Math.max(low, n));
const ease = n => { n = clamp(n); return n * n * (3 - 2 * n); };
const distance = (value, fallback, minimum) => Math.max(minimum, Number(value) || fallback);

export function buildTimeline(rooms, steps, travelPace = 3) {
  let cursor = 0;
  const segments = [];
  for (const room of rooms) {
    const duration = distance(room.duration, 90, 50) * clamp(Number(travelPace) || 3, 1, 5);
    segments.push({ kind: 'travel', room: room.key, id: `travel-${room.key}`, start: cursor, end: cursor + duration });
    cursor += duration;
    for (const step of steps.filter(item => item.room === room.key)) {
      const entry = distance(step.entry, 25, 10);
      const hold = distance(step.hold, 120, 60);
      const exit = distance(step.exit, 25, 10);
      segments.push({ kind: 'step', room: room.key, id: step.key, start: cursor,
        entryEnd: cursor + entry, holdEnd: cursor + entry + hold, end: cursor + entry + hold + exit });
      cursor += entry + hold + exit;
    }
  }
  return { segments, total: cursor };
}

export function stateAt(timeline, position) {
  const p = clamp(Number(position) || 0, 0, timeline.total);
  const index = timeline.segments.findIndex((segment, i) => p < segment.end || i === timeline.segments.length - 1);
  if (index < 0) return null;
  const segment = timeline.segments[index];
  const local = clamp((p - segment.start) / (segment.end - segment.start));
  if (segment.kind === 'travel') return { ...segment, index, local, phase: 'travel', opacity: 0 };
  const phase = p < segment.entryEnd ? 'entry' : p < segment.holdEnd ? 'hold' : 'exit';
  const opacity = phase === 'entry' ? ease((p - segment.start) / (segment.entryEnd - segment.start))
    : phase === 'hold' ? 1 : 1 - ease((p - segment.holdEnd) / (segment.end - segment.holdEnd));
  return { ...segment, index, local, phase, opacity };
}

// SSR/Node can import the pure model without a DOM.
if (typeof window !== 'undefined' && !customElements.get('totoka-j2s26')) {
  class Journey extends HTMLElement {
    static mediaOwner = null;
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.presentationTasks = new Set();
      this.stage = this.querySelector('.ttj2__stage');
      this.video = this.querySelector('video');
      this.status = this.querySelector('[data-status]');
      this.meter = this.querySelector('progress');
      this.rooms = [...this.querySelectorAll('[data-room]')].map(el => ({ ...el.dataset, key: el.dataset.room, el }));
      this.steps = [...this.querySelectorAll('[data-step]')].map(el => ({ ...el.dataset, key: el.dataset.step, room: el.dataset.roomKey, el }));
      this.timeline = buildTimeline(this.rooms, this.steps, this.dataset.travelPace);
      this.steps = this.timeline.segments.filter(s => s.kind === 'step').map(segment => this.steps.find(step => step.key === segment.id));
      // Match server-reserved distance; also tolerate an older section during installation.
      this.style.setProperty('--ttj2-distance', this.timeline.total);
      this.roomMap = new Map(this.rooms.map(r => [r.key, r]));
      this.stepMap = new Map(this.steps.map(s => [s.key, s]));
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.frame = 0;
      this.reader = false;
      this.lastState = null; this.activeIndex = null; this.armed = false;
      // Element scroll events do not bubble to window. Capture them on document,
      // and only react to the native scroll root currently containing this stage.
      this.on(document, 'scroll', event => {
        if (event.target === this.scrollRoot || (this.scrollRoot === window && event.target === document)) this.handleScroll();
      }, { passive: true, capture: true });
      this.on(window, 'resize', () => { this.measure(); this.schedule(); }, { passive: true });
      // Keep the cinematic stage outside the theme's generic reveal animation.
      // A 20-screen section cannot satisfy a generic 8% visibility threshold.
      const wrapper = this.closest('.shopify-section');
      (wrapper || this).setAttribute('data-totoka-no-reveal', '');
      (wrapper || this).removeAttribute('data-totoka-reveal');
      this.querySelectorAll('[data-totoka-reveal]').forEach(el => el.removeAttribute('data-totoka-reveal'));
      this.on(document, 'visibilitychange', () => document.hidden ? this.suspend() : this.schedule());
      this.on(this.reduced, 'change', () => this.setMode());
      this.on(this.querySelector('[data-reader]'), 'click', () => {
        this.reader = true; this.setMode();
        this.steps[0]?.el.scrollIntoView({ behavior: 'instant', block: 'start' });
        this.steps[0]?.el.querySelector('h2')?.focus({ preventScroll: true });
      });
      this.on(this, 'shopify:block:select', event => {
        const id = event.detail?.blockId;
        const segment = this.timeline.segments.find(s => (s.kind === 'step' ? this.stepMap.get(s.id) : this.roomMap.get(s.room))?.blockId === id);
        if (!segment || this.isReading) return;
        const position = segment.kind === 'step' ? (segment.entryEnd + segment.holdEnd) / 2 : (segment.start + segment.end) / 2;
        this.measure();
        const stepIndex = this.steps.findIndex(item => item.key === segment.id);
        const roomIndex = this.steps.findIndex(item => item.room === segment.room);
        this.goToStep(stepIndex >= 0 ? stepIndex : roomIndex, true);
      });
      this.on(this.video, 'loadedmetadata', () => this.schedule());
      this.on(this.video, 'loadeddata', () => { this.videoHasFrame = this.video.readyState >= 2; this.schedule(); });
      this.on(this.video, 'seeked', () => { if (this.video.readyState >= 2) this.videoHasFrame = true; this.schedule(); });
      this.on(this.video, 'error', () => { this.videoFailed = true; this.video.style.opacity = '0'; });
      this.resizeObserver = new ResizeObserver(() => { this.measure(); this.schedule(); });
      this.resizeObserver.observe(this.stage);
      this.resizeObserver.observe(this);
      this.measure();
      this.setMode();
    }

    on(target, type, handler, options = {}) { target?.addEventListener(type, handler, { ...options, signal: this.signal }); }
    findScrollRoot() {
      // Match the ancestor that owns sticky positioning, without theme-specific names.
      // Do not require scrollHeight > clientHeight: layout may still be settling.
      for (let parent = this.parentElement; parent && parent !== document.body && parent !== document.documentElement; parent = parent.parentElement) {
        if (/^(auto|scroll|overlay)$/.test(getComputedStyle(parent).overflowY)) return parent;
      }
      return window;
    }
    viewportTop() {
      return this.scrollRoot === window ? 0 : this.scrollRoot.getBoundingClientRect().top + this.scrollRoot.clientTop;
    }
    measure() {
      this.top = parseFloat(getComputedStyle(this).getPropertyValue('--ttj2-top')) || 0;
      this.range = Math.max(1, this.offsetHeight - this.stage.offsetHeight);
      const root = this.findScrollRoot();
      if (root !== this.scrollRoot || !this.observer) {
        this.observer?.disconnect();
        this.suspend();
        this.scrollRoot = root;
        this.visible = false;
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) this.schedule(); else this.suspend();
        }, { root: root === window ? null : root, threshold: 0 });
        this.observer.observe(this);
      }
    }
    scrollToPosition(position) {
      const current = this.scrollRoot === window ? window.scrollY : this.scrollRoot.scrollTop;
      const top = current + this.getBoundingClientRect().top - this.viewportTop() - this.top
        + position / this.timeline.total * this.range;
      this.scrollRoot.scrollTo({ top, behavior: 'instant' });
    }
    schedule() {
      if (!this.visible || document.hidden || this.isReading || this.frame || !this.abort) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
    }
    suspend() {
      clearTimeout(this.gestureTimer); this.armed = false;
      this.cancelTravel?.();
      this.cancelPresentation();
      cancelAnimationFrame(this.frame); this.frame = 0;
      this.video.pause(); // Immediately stop presentation; pending seek events cannot restart work.
    }
    hydrate(scope) {
      if (!scope) return;
      // Source before img prevents an unnecessary desktop request on mobile.
      scope.querySelectorAll('source[data-srcset]').forEach(el => { el.srcset = el.dataset.srcset; delete el.dataset.srcset; });
      scope.querySelectorAll('img[data-src]').forEach(el => {
        el.src = el.dataset.src; delete el.dataset.src;
        this.on(el, 'error', () => { el.style.visibility = 'hidden'; });
      });
    }
    releaseVideo() {
      this.video.pause();
      this.video.style.opacity = '0';
      if (this.video.hasAttribute('src')) { this.video.removeAttribute('src'); this.video.load(); }
      this.videoKey = null; this.seekTarget = null; this.videoHasFrame = false;
      if (Journey.mediaOwner === this) Journey.mediaOwner = null;
    }
    prepareVideo(room) {
      if (!room?.video) { this.releaseVideo(); return; }
      if (this.videoKey === room.key) return;
      Journey.mediaOwner?.releaseVideo();
      this.releaseVideo();
      Journey.mediaOwner = this;
      this.videoFailed = false;
      this.videoKey = room.key;
      this.video.preload = 'auto'; // One nearby/current clip only; never a second media element.
      this.video.src = room.video;
      this.video.load();
    }
    setMode() {
      this.isReading = this.reader || this.reduced.matches || this.rooms.length !== 4 || this.steps.length !== 10;
      this.dataset.reading = String(this.isReading);
      this.readerObserver?.disconnect();
      this.querySelector('[data-reader]').hidden = this.isReading;
      for (const step of this.steps) {
        step.el.inert = !this.isReading;
        step.el.setAttribute('aria-hidden', String(!this.isReading));
        step.el.querySelector('h2')?.setAttribute('tabindex', '-1');
      }
      this.lastState = null;
      if (this.isReading) {
        this.suspend(); this.releaseVideo();
        this.readerObserver = new IntersectionObserver(entries => {
          for (const entry of entries) if (entry.isIntersecting) { this.hydrate(entry.target); this.readerObserver.unobserve(entry.target); }
        }, { rootMargin: '200px 0px' });
        this.steps.forEach(step => this.readerObserver.observe(step.el));
      } else { this.activeIndex = null; this.pendingIndex = null; this.measure(); this.schedule(); }
    }
    showRoom(key, poster = false) {
      for (const room of this.rooms) {
        const active = room.key === key;
        room.el.classList.toggle('is-current', active);
        room.el.classList.toggle('is-poster', active && poster && Boolean(room.el.querySelector('[data-poster-picture]')));
      }
      const room = this.roomMap.get(key);
      this.hydrate(room?.el.querySelector(poster && room?.el.querySelector('[data-poster-picture]') ? '[data-poster-picture]' : '[data-room-picture]'));
    }
    scrollOffset() { return this.scrollRoot === window ? window.scrollY : this.scrollRoot.scrollTop; }
    settleGesture() {
      clearTimeout(this.gestureTimer);
      this.armed = false;
      this.gestureTimer = setTimeout(() => {
        if (!this.abort || this.busy || this.isReading) return;
        this.scrollAnchor = this.scrollOffset();
        this.armed = true;
      }, 240);
    }
    handleScroll() {
      if (this.isReading || !this.visible) return;
      if (this.activeIndex == null) { this.schedule(); return; }
      if (this.busy || !this.armed) { this.settleGesture(); return; }
      const delta = this.scrollOffset() - this.scrollAnchor;
      if (Math.abs(delta) < 28) return;
      const direction = Math.sign(delta);
      const next = this.activeIndex + direction;
      // At the ends the page remains free to scroll into the adjacent sections.
      if (next < 0 || next >= this.steps.length) return;
      const rect = this.getBoundingClientRect();
      const viewportHeight = this.scrollRoot === window ? window.innerHeight : this.scrollRoot.clientHeight;
      if (rect.bottom < this.viewportTop() || rect.top > this.viewportTop() + viewportHeight) return;
      this.goToStep(next);
    }
    stepPosition(index) {
      const step = this.timeline.segments.find(s => s.kind === 'step' && s.id === this.steps[index].key);
      return (step.entryEnd + step.holdEnd) / 2;
    }
    drawStep(index) {
      const chosen = this.steps[index];
      this.showRoom(chosen.room);
      for (const step of this.steps) {
        const active = step === chosen;
        step.el.classList.toggle('is-current', active);
        step.el.inert = !active;
        step.el.setAttribute('aria-hidden', String(!active));
        step.el.style.setProperty('--ttj2-opacity', active ? '1' : '0');
        step.el.style.setProperty('--ttj2-shift', '0px');
        if (active) this.hydrate(step.el);
      }
      this.status.textContent = this.roomMap.get(chosen.room).title;
      this.dataset.state = chosen.key;
      this.dataset.phase = 'hold';
      this.meter.value = (index + 1) / this.steps.length;
    }
    async goToStep(index, direct = false) {
      if (!this.abort || this.isReading || index < 0 || index >= this.steps.length) return;
      if (this.busy && !direct) return;
      this.cancelTravel?.();
      this.cancelPresentation();
      const token = this.transitionToken = (this.transitionToken || 0) + 1;
      const previous = this.activeIndex;
      const destination = this.steps[index];
      const from = previous == null ? null : this.steps[previous];
      const changingRoom = !from || from.room !== destination.room;
      const reverse = previous != null && index < previous;
      this.busy = true; this.armed = false; this.pendingIndex = index;
      clearTimeout(this.gestureTimer);
      // Compact gesture navigation reuses the section's original reserved distances.
      this.scrollToPosition(this.stepPosition(index));
      this.hydrate(destination.el);
      this.hydrate(this.roomMap.get(destination.room)?.el);
      let completed = true;
      if (changingRoom && !direct) {
        for (const step of this.steps) {
          step.el.classList.remove('is-current'); step.el.inert = true;
          step.el.setAttribute('aria-hidden', 'true');
        }
        this.dataset.phase = 'travel';
        this.status.textContent = this.roomMap.get(destination.room).title;
        // In reverse, retrace the clip that originally entered the current room.
        const room = this.roomMap.get(reverse ? from.room : destination.room);
        completed = await this.playTravel(room, reverse);
      }
      if (!this.abort || token !== this.transitionToken) return;
      if (!completed) { this.busy = false; return; }
      if (changingRoom && !direct) {
        completed = await this.arriveAt(destination, token);
        if (!this.abort || token !== this.transitionToken) return;
        if (!completed || this.isReading) { this.busy = false; return; }
      }
      this.releaseVideo();
      this.activeIndex = index;
      this.drawStep(index);
      if (changingRoom && !direct) {
        this.dataset.phase = 'arrival';
        completed = await this.revealArrival(destination);
        if (!this.abort || token !== this.transitionToken) return;
        if (!completed || this.isReading) { this.busy = false; return; }
      }
      this.pendingIndex = null;
      this.busy = false;
      this.dataset.phase = 'hold';
      this.scrollToPosition(this.stepPosition(index));
      this.settleGesture();
    }

    cancelPresentation() {
      // Resolves every pending await, including decode waits and animations.
      // Reader mode / hidden tabs / editor removal never leave an invisible step.
      for (const cancel of [...(this.presentationTasks || [])]) cancel();
    }
    waitForImage(scope) {
      this.hydrate(scope);
      const img = scope?.matches?.('img') ? scope : scope?.querySelector('img');
      if (!img || (!img.currentSrc && !img.getAttribute('src'))) return Promise.resolve(true);
      return new Promise(resolve => {
        let done = false, timer;
        const finish = value => {
          if (done) return;
          done = true; clearTimeout(timer);
          img.removeEventListener('load', loaded); img.removeEventListener('error', failed);
          this.presentationTasks.delete(cancel); resolve(value);
        };
        const cancel = () => finish(false);
        // Decode failure must not block text, links or onward navigation.
        const failed = () => finish(true);
        const loaded = () => {
          if (img.decode) img.decode().then(() => finish(true), failed);
          else finish(true);
        };
        this.presentationTasks.add(cancel);
        img.addEventListener('load', loaded); img.addEventListener('error', failed);
        timer = setTimeout(() => finish(true), 1800);
        if (img.complete) loaded();
      });
    }
    animateArrivalElement(element, frames, options) {
      if (!element?.animate || this.reduced.matches || this.isReading) return Promise.resolve(true);
      return new Promise(resolve => {
        let done = false;
        const animation = element.animate(frames, { fill: 'both', easing: 'cubic-bezier(.22,.61,.36,1)', ...options });
        const finish = value => {
          if (done) return;
          done = true; this.presentationTasks.delete(cancel);
          // All underlying styles already represent the final state, except
          // the video, which is released in the same microtask before repaint.
          animation.cancel(); resolve(value);
        };
        const cancel = () => finish(false);
        this.presentationTasks.add(cancel);
        animation.finished.then(() => finish(true), () => finish(false));
      });
    }
    async arriveAt(destination, token) {
      const room = this.roomMap.get(destination.room);
      const ready = await Promise.all([
        this.waitForImage(room?.el.querySelector('[data-room-picture]')),
        this.waitForImage(destination.el.querySelector('.ttj2__visual'))
      ]);
      if (!this.abort || token !== this.transitionToken || this.isReading || ready.includes(false)) return false;
      // Keep the actual last decoded video frame above the destination room.
      // Setting state also removes the initial poster on the first arrival.
      this.dataset.phase = 'arrival';
      this.dataset.state = destination.key;
      this.showRoom(destination.room);
      const hasFrame = this.videoHasFrame && this.video.readyState >= 2 && !this.videoFailed;
      const light = this.pearlPassage();
      let dissolve;
      if (hasFrame) {
        const baseFilter = getComputedStyle(this.video).filter;
        const softFilter = (baseFilter === 'none' ? '' : baseFilter + ' ') + 'blur(2.2px)';
        dissolve = this.animateArrivalElement(this.video, [
          { opacity: 1, filter: baseFilter, offset: 0 },
          { opacity: .82, filter: softFilter, offset: .3 },
          { opacity: 0, filter: baseFilter, offset: 1 }
        ], { duration: 640 }).then(completed => {
          // The light outlasts the dissolve. Keep the video hidden while it ends;
          // do not let an old editor transition change a newly prepared video.
          if (completed && token === this.transitionToken) this.video.style.opacity = '0';
          return completed;
        });
      } else {
        dissolve = this.animateArrivalElement(room?.el,
          [{ opacity: 0 }, { opacity: 1 }], { duration: 420 });
      }
      const results = await Promise.all([light, dissolve]);
      return results.every(Boolean);
    }
    async pearlPassage() {
      if (this.reduced.matches || this.isReading || !this.stage?.animate) return true;
      const veil = document.createElement('div');
      veil.className = 'ttj2__arrival-light';
      veil.setAttribute('aria-hidden', 'true');
      // A temporary light layer only: no extra media, no backdrop filtering,
      // and no changes to the room or icon's editor-controlled transforms.
      veil.style.cssText = 'position:absolute;inset:-12%;z-index:1;pointer-events:none;opacity:0;'
        + 'background:radial-gradient(ellipse at 32% 25%,rgba(255,255,255,.96) 0%,rgba(231,240,246,.72) 23%,rgba(215,225,236,.28) 49%,transparent 73%),'
        + 'linear-gradient(112deg,transparent 16%,rgba(242,247,250,.52) 42%,rgba(221,231,241,.2) 58%,transparent 80%);'
        + 'will-change:opacity,transform;';
      this.stage.appendChild(veil);
      try {
        return await this.animateArrivalElement(veil, [
          { opacity: 0, transform: 'translate3d(-7%,-2%,0) scale(1.02)', offset: 0 },
          { opacity: .42, transform: 'translate3d(-1%,0,0) scale(1.04)', offset: .36 },
          { opacity: .18, transform: 'translate3d(5%,1%,0) scale(1.05)', offset: .68 },
          { opacity: 0, transform: 'translate3d(9%,2%,0) scale(1.06)', offset: 1 }
        ], { duration: 820, easing: 'ease-in-out' });
      } finally {
        veil.remove();
      }
    }
    revealArrival(destination) {
      // Animate the inner picture so editor position, scale and rotation survive.
      const visual = destination.el.querySelector('.ttj2__visual picture') || destination.el.querySelector('.ttj2__visual');
      const copy = destination.el.querySelector('.ttj2__copy');
      return Promise.all([
        this.animateArrivalElement(visual, [
          { opacity: 0, translate: '0 12px' }, { opacity: 1, translate: '0 0' }
        ], { duration: 420 }),
        this.animateArrivalElement(copy, [
          { opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }
        ], { duration: 360, delay: 100 })
      ]).then(results => results.every(Boolean));
    }

    playTravel(room, reverse) {
      if (!room?.video) return Promise.resolve(true);
      this.prepareVideo(room);
      const v = this.video;
      v.muted = true; v.playsInline = true; v.loop = false;
      return new Promise(resolve => {
        let frame = 0, timeout = 0, finished = false, started = false, startTime = null;
        const finish = value => {
          if (finished) return;
          finished = true; cancelAnimationFrame(frame); clearTimeout(timeout); v.pause();
          v.removeEventListener('loadedmetadata', start); v.removeEventListener('error', fail);
          this.cancelTravel = null; resolve(value);
        };
        const fail = () => { this.videoFailed = true; finish(true); }; // A failed clip must not block the collection links.
        this.cancelTravel = () => finish(false);
        const start = () => {
          if (started || !Number.isFinite(v.duration) || v.duration <= 0) return;
          started = true;
          const end = Math.max(0.01, v.duration - 0.04);
          const seconds = clamp(Number(this.dataset.travelSeconds) || 2.2, 1, 6);
          v.currentTime = reverse ? end : 0;
          v.playbackRate = clamp(end / seconds, 0.5, 4);
          const tick = now => {
            if (!this.abort || this.isReading) { finish(false); return; }
            if (v.readyState >= 2 && !v.seeking) {
              this.videoHasFrame = true; v.style.opacity = '1';
              if (reverse) {
                if (startTime === null) startTime = now;
                const target = Math.max(0, end * (1 - (now - startTime) / (seconds * 1000)));
                if (v.currentTime <= 0.045 && target === 0) { finish(true); return; }
                if (Math.abs(v.currentTime - target) > 0.045 || target === 0) v.currentTime = target;
              } else if (v.currentTime >= end || v.ended) { finish(true); return; }
            }
            frame = requestAnimationFrame(tick);
          };
          if (!reverse) v.play()?.catch(fail);
          frame = requestAnimationFrame(tick);
        };
        timeout = setTimeout(fail, 15000);
        v.addEventListener('loadedmetadata', start); v.addEventListener('error', fail);
        start();
      });
    }
    render() {
      if (!this.visible || this.isReading || document.hidden || this.busy) return;
      if (this.pendingIndex != null) { this.goToStep(this.pendingIndex); return; }
      if (this.activeIndex != null) { if (!this.armed) this.settleGesture(); return; }
      const position = (this.viewportTop() + this.top - this.getBoundingClientRect().top) / this.range * this.timeline.total;
      if (position < 0) return;
      // Opening the page midway should restore that step instead of jumping to the start.
      const state = stateAt(this.timeline, position);
      if (state?.index > 0) {
        const index = state.kind === 'step' ? this.steps.findIndex(s => s.key === state.id)
          : this.steps.findIndex(s => s.room === state.room);
        this.goToStep(Math.max(0, index), true);
      } else this.goToStep(0);
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.suspend(); ++this.transitionToken; this.releaseVideo(); this.abort.abort(); this.abort = null;
      this.observer.disconnect(); this.resizeObserver.disconnect(); this.readerObserver?.disconnect();
    }
  }
  customElements.define('totoka-j2s26', Journey);
}